import { useEffect, useState } from "react";
import Seo from "@/lib/seo";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { CheckCircle, ArrowRight, CircleNotch } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!session_id) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (attempts++ < 20) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch { setStatus("error"); }
    };
    poll();
  }, [session_id, refresh]);

  return (
    <>
      <Seo title="Payment Successful" path="/payment/success" noindex description="Payment successful." />
      <Navbar />
      <div className="min-h-[calc(100vh-73px)] flex items-center justify-center p-8">
        <div className="max-w-lg text-center border border-zinc-800 p-12" data-testid="payment-success-card">
          {status === "checking" && (
            <>
              <CircleNotch size={48} weight="duotone" className="text-red-500 mx-auto mb-6 animate-spin" />
              <h1 className="font-display text-3xl uppercase tracking-tighter mb-3">Confirming Payment</h1>
              <p className="text-zinc-500 text-sm">Hang tight — talking to Stripe.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle size={56} weight="duotone" className="text-green-500 mx-auto mb-6" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">— Welcome to the roster</div>
              <h1 className="font-display text-4xl uppercase tracking-tighter mb-4">You're in.</h1>
              <p className="text-zinc-400 text-sm mb-8">Your subscription is active. Time to release something the world needs to hear.</p>
              <Link to="/dashboard" data-testid="success-to-dashboard"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase btn-brutal btn-brutal-red">
                Go to Dashboard <ArrowRight size={14} weight="bold" />
              </Link>
            </>
          )}
          {(status === "pending" || status === "error") && (
            <>
              <h1 className="font-display text-3xl uppercase tracking-tighter mb-3">
                {status === "pending" ? "Still Processing" : "Something Went Sideways"}
              </h1>
              <p className="text-zinc-500 text-sm mb-6">
                {status === "pending" ? "Payment is still being verified. Refresh in a minute." : "We couldn't confirm your payment. Please contact support."}
              </p>
              <Link to="/dashboard" className="text-red-500 text-xs tracking-[0.2em] uppercase hover:underline">Go to Dashboard →</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
